import {fetchCAPI, FetchCAPIError} from '@github-ui/copilot-shared-ui/capi/fetch-capi'

import {isMcpServerConfig, mergeMcpServerForm, type McpServerMap} from '../types/mcp'

/**
 * Personal settings in mission control.
 *
 * Requires `copilot_mission_control_use_api_gateway` for the host and token, and
 * the actor-scoped `copilot_mission_control_settings_api` rollout on the server.
 *
 * Two rules from the API description drive this whole module.
 *
 * PUT replaces the document. "Omitted settings remove their local values", so
 * every write has to read first and carry forward the parts it is not editing.
 * Writing MCP servers without merging would silently drop the user's model
 * preference.
 *
 * Writes are conditional. Creating the first version needs `If-None-Match: *`,
 * and updating needs `If-Match` with the last version. Sending neither is a 428.
 *
 * Saved settings are preferences, not applied state. Nothing here tells you a
 * server is reachable or that a session picked it up.
 */

const USER_SETTINGS_PATH = '/agents/settings/user'

const JSON_HEADERS = {Accept: 'application/json'}
const SETTINGS_DOCUMENTATION_URL =
  'https://github.com/github/copilot-mission-control/blob/main/internal/settings/README.md'
const MCP_SCHEMA_VERSION = 2

export interface SettingsAgent {
  model?: string
}

/** The API treats MCP contents as opaque JSON. Preserve fields this UI does not edit. */
export interface SettingsValues {
  agent?: SettingsAgent
  mcp?: {mcpServers?: McpServerMap; [key: string]: unknown}
}

/** Response body of `GET`/`PUT /agents/settings/user`. */
export interface UserSettings {
  schema_version: number
  /** Mission-control-assigned, immutable, and not writable. */
  version: number
  values: SettingsValues
}

/** A read, plus the precondition needed to write it back. */
export interface UserSettingsSnapshot {
  values: SettingsValues
  /**
   * Quoted decimal version for `If-Match`, or `undefined` when no document
   * exists yet and the write must create one.
   */
  etag?: string
}

export class UnsupportedSettingsSchemaError extends Error {
  schemaVersion: number

  constructor(schemaVersion: number) {
    super(
      `Copilot settings use schema version ${schemaVersion}, which this page cannot safely update. Reload to get the latest version of the page.`,
    )
    this.name = 'UnsupportedSettingsSchemaError'
    this.schemaVersion = schemaVersion
  }
}

/** Raised when the document changed between the read and the write. */
export class SettingsConflictError extends Error {
  constructor() {
    super('Your settings changed in another tab or window. Reload to see the latest, then try again.')
    this.name = 'SettingsConflictError'
  }
}

export class SettingsUnavailableError extends Error {
  constructor() {
    super('Personal MCP settings are not available.')
    this.name = 'SettingsUnavailableError'
  }
}

export class UnsafeSettingsError extends Error {
  constructor(message = 'Your settings contain data this page cannot safely edit.') {
    super(message)
    this.name = 'UnsafeSettingsError'
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

async function readSettings(response: Response): Promise<UserSettingsSnapshot> {
  const text = await response.text()
  const settings: unknown = JSON.parse(text)
  if (!isRecord(settings) || typeof settings.schema_version !== 'number') throw new UnsafeSettingsError()
  if (settings.schema_version !== 1 && settings.schema_version !== MCP_SCHEMA_VERSION) {
    throw new UnsupportedSettingsSchemaError(settings.schema_version)
  }

  // Skip JSON strings, including escaped quotes. Refuse number tokens that
  // JSON.stringify would change, rather than round opaque settings on a PUT.
  for (const match of text.matchAll(/"(?:[^"\\]|\\.)*"|(-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?)/g)) {
    if (match[1] !== undefined && JSON.stringify(Number(match[1])) !== match[1]) {
      throw new UnsafeSettingsError('Your settings contain a number this page cannot safely preserve.')
    }
  }

  const {values, version} = settings
  if (
    !isRecord(values) ||
    typeof version !== 'number' ||
    !Number.isSafeInteger(version) ||
    version < 1 ||
    (values.agent !== undefined &&
      (!isRecord(values.agent) || (values.agent.model !== undefined && typeof values.agent.model !== 'string'))) ||
    (values.mcp !== undefined && (!isRecord(values.mcp) || settings.schema_version === 1))
  ) {
    throw new UnsafeSettingsError()
  }
  if (
    isRecord(values.mcp) &&
    values.mcp.mcpServers !== undefined &&
    (!isRecord(values.mcp.mcpServers) || !Object.values(values.mcp.mcpServers).every(isMcpServerConfig))
  ) {
    throw new UnsafeSettingsError()
  }

  return {values, etag: response.headers.get('ETag') ?? `"${version}"`}
}

/**
 * Reads personal settings.
 *
 * Only the settings handler's documented 404 means no document exists.
 * Rollout gates and missing routes also return 404, without that error body.
 */
export async function fetchUserSettings(signal?: AbortSignal): Promise<UserSettingsSnapshot> {
  let response: Response
  try {
    response = await fetchCAPI({
      path: USER_SETTINGS_PATH,
      additionalHeaders: JSON_HEADERS,
      signal,
    })
  } catch (error) {
    if (error instanceof FetchCAPIError && error.status === 404) {
      if (error.documentationUrl === SETTINGS_DOCUMENTATION_URL && error.responseMessage === 'not found') {
        return {values: {}}
      }
      throw new SettingsUnavailableError()
    }
    throw error
  }

  return readSettings(response)
}

async function putUserSettings(
  values: SettingsValues,
  etag: string | undefined,
  signal?: AbortSignal,
): Promise<UserSettingsSnapshot> {
  // Exactly one precondition. `If-None-Match: *` creates, `If-Match` updates.
  const precondition: Record<string, string> = etag ? {'If-Match': etag} : {'If-None-Match': '*'}

  const response = await fetchCAPI({
    path: USER_SETTINGS_PATH,
    method: 'PUT',
    body: {schema_version: MCP_SCHEMA_VERSION, values},
    additionalHeaders: {...JSON_HEADERS, 'Content-Type': 'application/json', ...precondition},
    signal,
  })

  return readSettings(response)
}

/** 412 is a failed precondition, 428 is a missing one. Both mean reread and retry. */
function isPreconditionFailure(error: unknown): boolean {
  return error instanceof FetchCAPIError && (error.status === 412 || error.status === 428)
}

/**
 * Reads, applies `update` to the values, and writes the whole document back.
 *
 * Retries once on a precondition failure, since the common cause is a concurrent
 * write from another tab and the second attempt sees the newer document. A
 * second failure surfaces rather than looping, because repeated conflicts mean
 * something is actively fighting this write.
 */
export async function updateUserSettings(
  update: (values: SettingsValues) => SettingsValues,
  signal?: AbortSignal,
): Promise<UserSettingsSnapshot> {
  for (let attempt = 0; attempt < 2; attempt++) {
    const current = await fetchUserSettings(signal)
    try {
      return await putUserSettings(update(current.values), current.etag, signal)
    } catch (error) {
      if (isPreconditionFailure(error) && attempt === 0) continue
      if (isPreconditionFailure(error)) throw new SettingsConflictError()
      throw error
    }
  }

  throw new SettingsConflictError()
}

/** The user's personal MCP servers. Empty when none are saved. */
export async function fetchMcpServers(signal?: AbortSignal): Promise<McpServerMap> {
  const snapshot = await fetchUserSettings(signal)
  return snapshot.values.mcp?.mcpServers ?? {}
}

/**
 * Writes `mcpServers` derived from whatever is currently stored, preserving
 * every other setting.
 *
 * `revise` receives the stored map and returns the replacement. Taking a
 * function rather than a map keeps the read and the write inside one
 * precondition, so a retry recomputes against the document it is about to
 * overwrite instead of an older one.
 *
 * Removing the last server leaves unrelated MCP fields untouched.
 */
async function reviseMcpServers(
  revise: (servers: McpServerMap) => McpServerMap,
  signal?: AbortSignal,
): Promise<McpServerMap> {
  const snapshot = await updateUserSettings(values => {
    const next: SettingsValues = {...values}
    const servers = revise(values.mcp?.mcpServers ?? {})

    const mcp: NonNullable<SettingsValues['mcp']> = {...values.mcp, mcpServers: servers}
    if (Object.keys(servers).length === 0) {
      delete mcp.mcpServers
    }
    if (Object.keys(mcp).length === 0) delete next.mcp
    else next.mcp = mcp
    return next
  }, signal)

  return snapshot.values.mcp?.mcpServers ?? {}
}

/** Replaces the whole map. */
export async function saveMcpServers(servers: McpServerMap, signal?: AbortSignal): Promise<McpServerMap> {
  return reviseMcpServers(() => servers, signal)
}

/** Adds or edits one server, including an atomic rename. */
export async function putMcpServer(
  name: string,
  config: McpServerMap[string],
  editingName?: string,
  signal?: AbortSignal,
): Promise<McpServerMap> {
  return reviseMcpServers(current => {
    if (name !== editingName && Object.hasOwn(current, name)) {
      throw new UnsafeSettingsError('A server with this name already exists. Choose another name.')
    }
    if (editingName !== undefined && !Object.hasOwn(current, editingName)) {
      throw new SettingsConflictError()
    }
    const next = {...current}
    const existing = editingName === undefined ? undefined : current[editingName]
    // Deleting the old key before reassigning moves `name` to the end, which
    // is only correct for an actual rename. An ordinary edit keeps `name`'s
    // existing position by reassigning it in place instead.
    if (editingName !== undefined && editingName !== name) delete next[editingName]
    next[name] = existing ? mergeMcpServerForm(existing, config) : config
    return next
  }, signal)
}

/** Removes one server by name. Removing an absent server is not an error. */
export async function deleteMcpServer(name: string, signal?: AbortSignal): Promise<McpServerMap> {
  return reviseMcpServers(current => {
    const next = {...current}
    delete next[name]
    return next
  }, signal)
}
